import * as _ from 'lodash';
import createEasyFirestore from 'vuex-easy-firestore';
import firebase from 'firebase/app';
import firebaseApp from './firebaseApp';
import router from '@/router';
import Vue from 'vue';
import Vuex from 'vuex';
import { Account } from './models/account';
import { Country } from './models/country';
import americaCountries from './countries';
import 'firebase/auth';
import { countBy } from 'lodash';

Vue.use(Vuex);

interface WTFState {
  instagramQuery: string;
  currentCountry: string;
  errorMessage: string;
  user: firebase.User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  countries: Country[];
}

const fireAccounts = {
  firestorePath: 'accounts',
  firestoreRefType: 'collection',
  moduleName: 'fireAccounts',
  statePropName: 'accounts',
  namespaced: true,
};

const easyFirestore = createEasyFirestore(fireAccounts, {logging: true});

const auth = firebase.auth(firebaseApp);

const initialState: WTFState = {
  instagramQuery: '',
  currentCountry: '',
  errorMessage: '',
  user: null,
  isAuthenticated: false,
  isAdmin: false,
  countries: americaCountries,
};

const orderBy = ['instagram'];

function fetchInstagramProfilePicture(account: Account) {
  const username = account.instagram.replace('@', '');
  fetch(`https://www.instagram.com/${username}/?__a=1`)
    .then((response) => {
        if (response.ok) {
          return response.json();
        }
      },
    )
    .then((data) => {
      account.isActive = true;
      account.profilePhotoUrl = data.graphql.user.profile_pic_url_hd as string;
      return account;
    })
    .then((updatedAccount) => {
      fetch(updatedAccount.profilePhotoUrl)
        .then((r) => r.arrayBuffer())
        .then((buffer) => {
          updatedAccount.profilePhoto = btoa(String.fromCharCode(...new Uint8Array(buffer)));
          saveAccount(updatedAccount);
        });
    })
    .catch((err: string) => {
      account.isActive = false;
      account.profilePhotoUrl = '';
      account.profilePhoto = '';
      saveAccount(account);
    });
}

function saveAccount(account: Account): Promise<any> {
  return store.dispatch('fireAccounts/patch',
    {
      id: account.id,
      country: account.country,
      instagram: account.instagram,
      proof: account.proof,
      profilePhotoUrl: account.profilePhotoUrl,
      reason: account.reason,
      isActive: account.isActive,
      profilePhoto: account.profilePhoto,
    },
  );
}

function matchesInstagram(state: WTFState, account: Account): boolean {
  if (state.instagramQuery === '' || state.instagramQuery === '@') {
    return true;
  } else {
    return _.includes(account.instagram, state.instagramQuery);
  }
}

const store = new Vuex.Store({
  state: initialState,
  mutations: {
    setErrorMessage(state: WTFState, payload) {
      state.errorMessage = payload;
    },
    setInstagramQuery(state: WTFState, payload) {
      state.instagramQuery = payload.toLowerCase();
    },
    setIsAuthenticated(state, payload) {
      state.isAuthenticated = payload;
    },
    setUser(state: WTFState, payload: firebase.auth.UserCredential) {
      if (payload) {
        state.user = payload.user;
      } else {
        state.user = null;
      }
    },
    setIsAdmin(state: WTFState, user: firebase.User) {
      // PROD
      if (user
        && (user.uid === 'bhespFfbHAV1Xl5hMplNVmkzr9z2' // mexican
          || user.uid === 'dLJXyLntCcfNzXLWjBDtHt69z2H2' // ghost
          || user.uid === 'mcLNiDniZ9RD9w2qPykiIpoaR5h2')) { // cuties
          state.isAdmin = true;
        } else {
          state.isAdmin = false;
        }
      // DEV
      // if (user && user.uid === 'lq2VMEKM8bRaOjdPO0BLMW1KRMo2' || user.uid === 'NXISY0sXPqaEjjTMOFaMjuMh9Ig2') {
      //   state.isAdmin = true;
      // } else {
      //   state.isAdmin = false;
      // }
    },
  },
  actions: {
    clearErrors({ commit }, {}) {
      commit('setErrorMessage', '');
    },
    executeQuery({ dispatch, state }) {
      const where = [];
      if (state.instagramQuery !== '' && state.instagramQuery !== '@') {
        where.push(['instagram', '==', `@${state.instagramQuery}`]);
        const limit = 16;
        dispatch('fireAccounts/fetchAndAdd', {where, limit});
      }
    },
    searchAccount({ commit, dispatch }, query) {
      commit('setInstagramQuery', query.profile);
      dispatch('executeQuery');
    },
    getAllAccounts({ commit, dispatch }) {
      dispatch('fireAccounts/fetchAndAdd', { limit: 0 });
    },
    userLogin({ commit }, { email, password }) {
      auth.setPersistence(firebase.auth.Auth.Persistence.LOCAL)
      .then(() => {
        auth
          .signInWithEmailAndPassword(email, password)
          .then((user: firebase.auth.UserCredential) => {
              commit('setUser', user);
              commit('setIsAuthenticated', true);
              commit('setErrorMessage', '');
              router.push('/search');
          })
          .catch((err) => {
              commit('setUser', null);
              commit('setIsAuthenticated', false);
              commit('setErrorMessage', err.message);
          });
      });
    },
    userSignOut({ commit }) {
      auth
        .signOut()
        .then(() => {
            commit('setUser', null);
            commit('setIsAuthenticated', false);
            commit('setErrorMessage', '');
            router.push('/sign-in');
        })
        .catch(() => {
            commit('setUser', null);
            commit('setIsAuthenticated', false);
            router.push('/');
        });
    },
  },
  getters: {
    isAuthenticated: (state) => {
      return state.isAuthenticated;
    },
    isAdmin: (state) => {
      return state.isAdmin;
    },
    getAccounts: (state) => {
      if ((state.instagramQuery) && (state as any).fireAccounts
        && (state as any).fireAccounts.accounts) {
        const accountsInFirestore = (state as any).fireAccounts.accounts;
        const accounts: Account[] = [];
        Object.keys(accountsInFirestore).forEach((accountId) => {
          const account = accountsInFirestore[accountId] as Account;
          if (matchesInstagram(state, account)) {
              accounts.push(account);
          }
          if (account.profilePhoto === undefined && _.includes(accounts, account)) {
            fetchInstagramProfilePicture(account);
          }
        });
        return accounts;
      } else {
        return [];
      }
    },
    getAllAccounts: (state) => (skip: number, take: number) => {
      const accountsInFirestore = (state as any).fireAccounts.accounts;
      const accounts: Account[] = [];
      Object.keys(accountsInFirestore).forEach((accountId) => {
          const account = accountsInFirestore[accountId] as Account;
          accounts.push(account);
      });
      return accounts;
    },
    getCountries: (state) => {
      return state.countries;
    },
    getCountriesNames: (state) => {
      return state.countries.map((c) => c.name).sort();
    },
  },
  plugins: [easyFirestore],
});

auth.onAuthStateChanged((user) => {
  if (user !== null) {
    store.commit('setUser', user);
    store.commit('setIsAuthenticated', true);
    store.commit('setIsAdmin', user);
    store.commit('setErrorMessage', '');
    router.push('/search');
  }
});

export default store;
